import React from "react";
import {
  Autocomplete,
  Checkbox,
  TextField,
  Chip,
  FilterOptionsState,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface OptionType {
  label: string;
  id: number | string;
  section?: string;
}

type FilterOptions<T> = (
  options: readonly T[],
  state: FilterOptionsState<T>,
) => T[];

interface AutocompleteElementProps {
  required?: boolean;
  options: (
    | OptionType
    | string
    | { id: boolean | number | string; label: string }
  )[];
  disabled?: boolean;
  multi: boolean;
  label?: string;
  size?: "small" | "medium";
  limit?: number;
  open?: boolean;
  sx?: object;
  loading?: boolean;
  value?:
    | OptionType
    | OptionType[]
    | { id: number | string | boolean; label: number | string | boolean }
    | string
    | string[]
    | null;
  onOpen?: () => void;
  onClose?: (_: any, reason: string) => void;
  onChange: any;
  showClear?: boolean;
  noOptionsText?: string;
  restricted?: boolean;
  allOptions?: { id: string; label: string }[];
  selectedIds?: string[];
  inputValue?: string;
  filterOptions?: FilterOptions<any>;
  defaultValue?: OptionType | OptionType[] | string | string[] | null;
  onInputChange?: (event: any, value: string, reason: string) => void;
  variant?: "standard" | "outlined" | "filled";
  groupBy?: (option: string | OptionType) => string;
  error?: boolean;
  helperText?: string;
  className?: string;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: any,
    state: { selected: boolean },
  ) => React.ReactNode;
}

const AutocompleteElement: React.FC<AutocompleteElementProps> = ({
  required,
  options,
  disabled,
  multi,
  label,
  size,
  limit,
  sx,
  value,
  onChange,
  loading,
  open,
  onOpen,
  onClose,
  showClear = "true",
  noOptionsText = "No options",
  restricted = false,
  allOptions,
  selectedIds,
  defaultValue,
  inputValue,
  filterOptions,
  onInputChange,
  variant = "outlined",
  groupBy,
  error = false,
  helperText,
  className,
  renderOption,
}) => {
  return (
    <Autocomplete
      open={open}
      options={options}
      multiple={multi}
      sx={sx}
      size={size}
      disabled={disabled}
      value={value}
      loading={loading}
      limitTags={limit}
      groupBy={groupBy}
      disableCloseOnSelect={multi}
      noOptionsText={noOptionsText}
      onOpen={onOpen}
      onClose={onClose}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === "string" || typeof value === "string") {
          return option === value;
        }
        return option.id === value.id;
      }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      filterOptions={filterOptions}
      renderOption={(props, option, state) => {
        if (renderOption) {
          return renderOption(props, option, state);
        }

        const { selected } = state;
        const { key, ...optionProps } = props;

        const uniqueKey =
          typeof option === "string"
            ? `string-${option}-${props["data-option-index"]}`
            : `id-${option.id}`;

        const isSelectAll =
          typeof option !== "string" && option.id === "select_all";

        const allSelected = selectedIds?.length === allOptions?.length;

        return (
          <li key={uniqueKey} {...optionProps}>
            {multi && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={isSelectAll ? allSelected : selected}
              />
            )}
            {typeof option === "string" ? option : option.label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          error={error}
          helperText={error ? helperText : ""}
          label={
            label ? (
              <span>
                {label}
                {required ? (
                  <span style={{ color: "#d32f2f", marginLeft: 2 }}>*</span>
                ) : null}
              </span>
            ) : undefined
          }
        />
      )}
      className={`${!showClear ? "hide-clear" : ""} ${className ?? ""}`}
      onChange={onChange}
      disableClearable={restricted}
      inputValue={inputValue}
      onInputChange={onInputChange}
      defaultValue={defaultValue ?? (multi ? [] : null)}
      renderTags={(value, getTagProps) => {
        if (!restricted) {
          return value.map((option: any, index: number) => (
            <Chip
              label={option.label}
              size="small"
              {...getTagProps({ index })}
              key={option.id}
            />
          ));
        }
        const displayed = value.slice(0, 1);
        const hiddenCount = value.length - displayed.length;

        return [
          ...displayed.map((option: any, index) => (
            <Chip
              label={option.label}
              size="small"
              {...getTagProps({ index })}
              key={option.id}
            />
          )),
          ...(hiddenCount > 0
            ? [
                <span
                  key="more"
                  style={{
                    marginLeft: 8,
                    fontSize: "14px",
                    lineHeight: "14px",
                  }}
                >
                  +{hiddenCount} more
                </span>,
              ]
            : []),
        ];
      }}
    />
  );
};

export default AutocompleteElement;
